import { CalendarRange } from 'lucide-react';

import { cn, formatDateValue, toIsoDate } from '@/lib/utils';

import {
  contarDias,
  MAX_DIAS_RANGO,
  RANGE_LABELS,
  type RangePreset,
} from '../hooks/use-date-range';
import type { DateRange } from '../dashboard.api';
import { CampoFecha } from '@/components/ui/calendario';

interface DateRangePickerProps {
  preset: RangePreset;
  onPresetChange: (preset: RangePreset) => void;
  /** Rango personalizado tal como lo escribió el usuario. */
  custom: DateRange;
  onCustomChange: (range: DateRange) => void;
  /** Rango efectivo que se consulta (puede estar recortado por el tope de días). */
  range: DateRange;
  /** Aviso cuando el rango personalizado se recortó al máximo permitido. */
  aviso?: string | null;
}

const PRESETS_RAPIDOS: readonly RangePreset[] = ['today', 'week', 'month', 'previous-month'];

const INPUT_CLASSES =
  'h-9 rounded-lg border border-ink-200 bg-white px-2 text-xs text-ink-900 transition-colors ' +
  'focus:border-brand-400 focus:ring-2 focus:ring-brand-100 focus:outline-none';

/**
 * Selector de rango de fechas del dashboard.
 *
 * Ofrece los presets rápidos y, además, un rango personalizado con fecha de
 * inicio y de fin. El rango personalizado se limita a `MAX_DIAS_RANGO` días y no
 * admite fechas futuras.
 */
export function DateRangePicker({
  preset,
  onPresetChange,
  custom,
  onCustomChange,
  range,
  aviso,
}: DateRangePickerProps) {
  const hoy = toIsoDate(new Date());
  const dias = contarDias(range.from, range.to);

  return (
    <div className="flex flex-col items-start gap-2 sm:items-end">
      <div
        role="group"
        aria-label="Rango de fechas"
        className="inline-flex w-fit flex-wrap gap-1 rounded-xl border border-ink-100 bg-white p-1 shadow-soft"
      >
        {PRESETS_RAPIDOS.map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={preset === option}
            onClick={() => onPresetChange(option)}
            className={cn(
              'cursor-pointer rounded-lg px-3.5 py-1.5 text-sm font-medium transition-colors',
              preset === option
                ? 'bg-brand-500 text-white shadow-sm'
                : 'text-ink-500 hover:bg-ink-100 hover:text-ink-800',
            )}
          >
            {RANGE_LABELS[option]}
          </button>
        ))}

        <button
          type="button"
          aria-pressed={preset === 'custom'}
          onClick={() => onPresetChange('custom')}
          className={cn(
            'inline-flex cursor-pointer items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-sm font-medium transition-colors',
            preset === 'custom'
              ? 'bg-brand-500 text-white shadow-sm'
              : 'text-ink-500 hover:bg-ink-100 hover:text-ink-800',
          )}
        >
          <CalendarRange className="size-3.5" aria-hidden="true" />
          {RANGE_LABELS.custom}
        </button>
      </div>

      {preset === 'custom' ? (
        <div className="rounded-xl border border-ink-100 bg-white px-3 py-2 shadow-soft">
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex flex-col gap-1 text-[11px] font-medium text-ink-500">
              Desde
              <CampoFecha
               
                value={custom.from}
                max={hoy}
                onChange={(event) => onCustomChange({ from: event.target.value, to: custom.to })}
                className={INPUT_CLASSES}
               tipo="date" />
            </label>

            <label className="flex flex-col gap-1 text-[11px] font-medium text-ink-500">
              Hasta
              <CampoFecha
               
                value={custom.to}
                min={custom.from}
                max={hoy}
                onChange={(event) => onCustomChange({ from: custom.from, to: event.target.value })}
                className={INPUT_CLASSES}
               tipo="date" />
            </label>

            <span className="pb-2 text-[11px] text-ink-400">
              {dias > 0 ? `${dias} ${dias === 1 ? 'día' : 'días'}` : '—'}
              <span className="text-ink-300"> · máx. {MAX_DIAS_RANGO}</span>
            </span>
          </div>

          <p className="mt-1.5 text-[11px] text-ink-500">
            Mostrando del {formatDateValue(range.from)} al {formatDateValue(range.to)}
          </p>
        </div>
      ) : null}

      {preset === 'custom' && aviso ? (
        <p className="text-[11px] text-amber-600">{aviso}</p>
      ) : null}
    </div>
  );
}
